/*
 * SolidForms — Build With Us, guided flow controller.
 *
 * Three steps on one page:
 *   1  who they are
 *   2  what they want made — description, and optionally a file the browser
 *      slices on the spot (assets/sf-instant-slicer.js does that work and
 *      announces results with a `sfsl:estimate` event)
 *   3  what it comes to — the review, the price slot, and submit
 *
 * The form itself is Shopify's native contact form, so a submission lands in
 * the shop's notifications with no app in between. Everything the visitor
 * entered plus the estimate travels in the message body.
 *
 * PRICING lives in exactly one place: priceFor() below. It returns null today,
 * which is what keeps the page honest — the team quotes. Drop the formula in
 * and the price appears; nothing else needs touching.
 */
(function () {
  'use strict';

  /* ==================================================================
     PRICING — 3D-Pi.
     ------------------------------------------------------------------
     The number comes from the shop's own 3D-Pi account: its rate card,
     its filament prices, its failure and profit margins. Asking the
     server keeps those rates out of the customer's browser.
     ================================================================== */
  function fmtMoney(v, currency) {
    if (typeof v !== 'number' || !isFinite(v)) return null;
    var cur = (currency || 'EGP') === 'EGP' ? 'LE' : currency;
    // Always approximate — the figure is a reference, never a commitment.
    return '≈ ' + cur + ' ' + Math.round(v).toLocaleString();
  }

  /*
   * What it COULD cost. The weight behind it is a geometry guess with a wide
   * band, so quoting one number would read as a promise the shop has not made.
   * The range is the same uncertainty, stated honestly.
   */
  function fmtRange(lo, hi, currency, atMin) {
    var a = typeof lo === 'number' && isFinite(lo) ? lo : null;
    var b = typeof hi === 'number' && isFinite(hi) ? hi : null;
    if (a === null && b === null) return null;
    /* The floor price is a price, not the bottom of a range — and not an
       approximation either, so it loses the "≈" the estimates carry. */
    if (atMin) {
      var v = a === null ? b : a;
      var c = (currency || 'EGP') === 'EGP' ? 'LE' : currency;
      return c + ' ' + Math.round(v).toLocaleString();
    }
    if (a === null || b === null) return fmtMoney(a === null ? b : a, currency);
    if (Math.round(a) === Math.round(b)) return fmtMoney(a, currency);
    var cur = (currency || 'EGP') === 'EGP' ? 'LE' : currency;
    return cur + ' ' + Math.round(a).toLocaleString() + ' – ' + cur + ' ' + Math.round(b).toLocaleString();
  }

  /*
   * A reference for every submission, including the ones with no file at all.
   * The slicer offers the same helper, but this must not depend on that script
   * having loaded — a customer who only wrote a description still needs
   * something to quote back at us.
   */
  function makeRef() {
    if (window.SFSlicer && window.SFSlicer.makeRef) return window.SFSlicer.makeRef();
    return 'SF-' + Date.now().toString(36).slice(-4).toUpperCase() +
      Math.floor(Math.random() * 1296).toString(36).toUpperCase().padStart(2, '0');
  }

  function fmtTime(min) {
    var m = Math.max(1, Math.round(min));
    if (m < 60) return m + ' min';
    var h = Math.floor(m / 60), r = m % 60;
    if (h < 24) return r ? h + 'h ' + (r < 10 ? '0' : '') + r : h + 'h';
    return Math.floor(h / 24) + 'd ' + (h % 24) + 'h';
  }

  /* ==================================================================
     DRAFT — surviving a refresh.
     ------------------------------------------------------------------
     Reloading the page used to throw the whole form away: the answers,
     the step, and every attached model. The typed answers go in
     sessionStorage; the file bytes are far too big for it, so they go in
     IndexedDB. Both are per-tab and both are cleared on submit.

     sessionStorage, not localStorage, on purpose: a draft holding someone's
     name and phone number should not outlive the tab they typed it in.
     ================================================================== */
  function T(key, fallback) {
    var m = window.SFSL_I18N;
    return (m && m[key]) || fallback;
  }

  var DRAFT_KEY = 'sfbf:draft';
  var DB_NAME = 'sfbf-draft';
  var STORE = 'files';

  function openDb() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) return reject(new Error('no indexedDB'));
      var rq = indexedDB.open(DB_NAME, 1);
      rq.onupgradeneeded = function () {
        if (!rq.result.objectStoreNames.contains(STORE)) rq.result.createObjectStore(STORE);
      };
      rq.onsuccess = function () { resolve(rq.result); };
      rq.onerror = function () { reject(rq.error); };
    });
  }

  function dbPut(value) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(value, 'current');
        tx.oncomplete = function () { db.close(); resolve(); };
        tx.onerror = function () { db.close(); reject(tx.error); };
      });
    });
  }

  function dbGet() {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE, 'readonly');
        var rq = tx.objectStore(STORE).get('current');
        rq.onsuccess = function () { db.close(); resolve(rq.result || null); };
        rq.onerror = function () { db.close(); reject(rq.error); };
      });
    });
  }

  function dbClear() {
    return openDb().then(function (db) {
      return new Promise(function (resolve) {
        var tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete('current');
        tx.oncomplete = function () { db.close(); resolve(); };
        tx.onerror = function () { db.close(); resolve(); };
      });
    }).catch(function () {});
  }

  /* Read a File back as the bytes we can store, and turn stored bytes back
     into a File so the slicer sees exactly what a drop would have given it. */
  function toRecord(file) {
    return file.arrayBuffer().then(function (buf) {
      return { name: file.name, type: file.type, buf: buf };
    });
  }
  function toFile(rec) {
    return new File([rec.buf], rec.name, { type: rec.type || 'application/octet-stream' });
  }

  function clearDraft() {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) {}
    dbClear();
  }

  function Flow(root) {
    if (root.sfBuildFlow) return;
    root.sfBuildFlow = true;

    var cfg = {};
    try { cfg = JSON.parse(root.getAttribute('data-sf-build-flow') || '{}'); } catch (e) {}

    /* Success screen: no steps, just the button that has to remember what the
       visitor was sending. Restore it and stop. */
    var restore = root.querySelector('[data-sfbf-wa-restore]');
    var orderSlot = root.querySelector('[data-sfbf-order]');
    if (restore || orderSlot) {
      var saved = null, savedOrder = null;
      try {
        saved = sessionStorage.getItem('sfbf:wa');
        savedOrder = sessionStorage.getItem('sfbf:order');
      } catch (e) {}

      if (restore && saved) restore.href = saved;

      if (orderSlot && savedOrder) {
        var num = orderSlot.querySelector('[data-sfbf-order-number]');
        var lbl = orderSlot.querySelector('[data-sfbf-order-label]');
        var kind = null;
        try { kind = sessionStorage.getItem('sfbf:order-kind'); } catch (e) {}
        if (num) num.textContent = savedOrder;
        // A priced order and a bare reference are different things, and calling
        // one the other would confuse whoever they quote it to.
        if (lbl && kind === 'ref') {
          lbl.textContent = lbl.getAttribute('data-ref-label') || 'Your reference';
        }
        orderSlot.hidden = false;
      }

      try {
        sessionStorage.removeItem('sfbf:wa');
        sessionStorage.removeItem('sfbf:order');
        sessionStorage.removeItem('sfbf:order-kind');
      } catch (e) {}
      return;
    }

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-sfbf-step]'));
    var rails = Array.prototype.slice.call(root.querySelectorAll('[data-sfbf-rail]'));
    var form = root.querySelector('[data-sfbf-form]');
    var prices = [];
    var estimate = null;
    var price;            // undefined = still asking, null = no price
    var priceLo = null, priceHi = null, priceCur = 'EGP', priceAtMin = false;
    var current = 0;
    var furthest = 0;

    function field(name) { return root.querySelector('[data-sfbf-field="' + name + '"]'); }
    function q(sel) { return root.querySelector(sel); }

    /* ------------------------------------------------------------ steps */

    function show(index) {
      current = Math.max(0, Math.min(steps.length - 1, index));
      furthest = Math.max(furthest, current);
      steps.forEach(function (el, i) { el.hidden = i !== current; });
      rails.forEach(function (el, i) {
        if (i === current) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
        el.setAttribute('data-done', String(i < current));
        el.setAttribute('data-reachable', String(i <= furthest));
      });
      if (current === 2) renderReview();
      var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
      var focusable = steps[current].querySelector('input, textarea, select, button');
      if (focusable && current > 0) focusable.focus({ preventScroll: true });
    }

    /* ------------------------------------------------------- validation */

    function setError(el, message) {
      var wrap = el.closest('.sfbf__field');
      var slot = wrap && wrap.querySelector('[data-sfbf-error]');
      if (wrap) wrap.classList.toggle('is-invalid', !!message);
      if (slot) slot.textContent = message || '';
      el.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    /* ------------------------------------------------------- student proof */

    /*
     * A discount that costs money should not rest on a checkbox, so saying yes
     * opens a required upload. The file rides along with the order and the team
     * checks it before the discount stands.
     */
    var student = { yes: false, file: null };
    var studentEls = {
      group: q('[data-sfbf-student]'),
      proof: q('[data-sfbf-student-proof]'),
      pick: q('[data-sfbf-student-pick]'),
      input: q('[data-sfbf-student-input]'),
      name: q('[data-sfbf-student-name]'),
      error: q('[data-sfbf-student-error]')
    };

    function setStudent(yes) {
      student.yes = yes;
      if (studentEls.group) {
        studentEls.group.querySelectorAll('[data-student]').forEach(function (b) {
          var on = (b.getAttribute('data-student') === 'yes') === yes;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', String(on));
        });
      }
      if (studentEls.proof) studentEls.proof.hidden = !yes;
      if (!yes) {
        student.file = null;
        if (studentEls.input) studentEls.input.value = '';
        if (studentEls.name) studentEls.name.textContent = '';
        if (studentEls.error) studentEls.error.textContent = '';
      }
    }

    if (studentEls.group) {
      studentEls.group.addEventListener('click', function (e) {
        var b = e.target.closest('[data-student]');
        if (b) setStudent(b.getAttribute('data-student') === 'yes');
      });
    }
    if (studentEls.pick && studentEls.input) {
      studentEls.pick.addEventListener('click', function () { studentEls.input.click(); });
    }
    if (studentEls.input) {
      studentEls.input.addEventListener('change', function (e) {
        var f = e.target.files && e.target.files[0];
        if (!f) return;
        if (f.size > 20 * 1024 * 1024) {
          if (studentEls.error) studentEls.error.textContent = T('err_photo_big', 'That file is over 20 MB — a photo is plenty.');
          return;
        }
        student.file = f;
        if (studentEls.name) studentEls.name.textContent = f.name;
        if (studentEls.error) studentEls.error.textContent = '';
      });
    }

    function validateStep(index) {
      var ok = true, firstBad = null;
      var required = steps[index].querySelectorAll('[data-sfbf-required]');
      Array.prototype.forEach.call(required, function (el) {
        var v = (el.value || '').trim();
        var message = '';
        if (!v) {
          message = el.getAttribute('data-sfbf-empty') || 'This one is needed.';
        } else if (el.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
          message = 'That email address does not look right.';
        } else if (el.getAttribute('data-sfbf-field') === 'phone' && v.replace(/\D/g, '').length < 8) {
          message = 'Add the full number, including the area code.';
        }
        setError(el, message);
        if (message) { ok = false; if (!firstBad) firstBad = el; }
      });
      // Saying you are a student is only accepted with something to check.
      if (index === 0 && student.yes && !student.file) {
        if (studentEls.error) {
          studentEls.error.textContent = T('err_student', 'Add your student ID to claim the discount, or choose No.');
        }
        if (studentEls.pick) studentEls.pick.focus();
        ok = false;
      }

      if (firstBad) firstBad.focus();
      return ok;
    }

    /* ---------------------------------------------------------- review */

    function row(key, value) {
      if (!value) return '';
      return '<div class="sfbf__review-row">' +
        '<span class="sfbf__review-key">' + key + '</span>' +
        '<span class="sfbf__review-val">' + value + '</span></div>';
    }
    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    /* The price panel. Its own function because it resolves asynchronously and
       must not be overtaken by an older, slower request. */
    /* There are two price panels — one beside the controls on step 2, one on
       step 3 — and they always say the same thing, so both are written at once. */
    function paintPrice(text, noteText, settled) {
      root.querySelectorAll('[data-sfbf-price]').forEach(function (box) {
        box.classList.toggle('sfbf__price--pending', !settled);
      });
      root.querySelectorAll('[data-sfbf-price-value]').forEach(function (el) {
        el.textContent = text;
      });
      root.querySelectorAll('[data-sfbf-price-note]').forEach(function (el) {
        el.textContent = noteText;
      });
    }

    function quoteNote() {
      return cfg.quoteNote ||
        'Send it over and a price comes back on WhatsApp, usually within one working day.';
    }

    /*
     * The slicer owns pricing now — it holds the parts, so it asks for each
     * one and the total in a single request and announces the answer. This
     * just paints whatever it last said.
     */
    function renderPrice() {
      if (!estimate) return paintPrice('Drop in your files to see the price', quoteNote(), false);
      if (!cfg.priceUrl) return paintPrice('The team prices this from your files', quoteNote(), false);
      if (price === undefined) {
        return paintPrice(cfg.pricingLabel || 'Working out your price…', '', false);
      }
      var shown = fmtRange(priceLo, priceHi, priceCur, priceAtMin);
      if (!shown) return paintPrice('The team prices this from your files', quoteNote(), false);

      paintPrice(
        shown,
        cfg.priceNote ||
          'The team confirms the exact figure before anything is printed.',
        true
      );
    }

    function renderReview() {
      var box = q('[data-sfbf-review]');
      var name = field('name'), phone = field('phone'), email = field('email');
      var desc = field('description');

      /* What the customer sees back: their own answers, and the two facts
         about the file they can verify with their own eyes. No weights, no
         times — those are working numbers behind the price. */
      var html =
        row('Name', esc(name && name.value)) +
        row('WhatsApp', esc(phone && phone.value)) +
        row('Email', esc(email && email.value) || '<em>not given</em>') +
        row('What to make', esc(desc && desc.value).replace(/\n/g, '<br>')) +
        (student.yes ? row('Student', 'Discount requested &mdash; ID attached for us to check') : '');

      if (estimate) {
        var list = estimate.files || [];
        html += row('Reference', '<strong>' + esc(estimate.ref) + '</strong>');

        if (list.length > 1) {
          html += row(
            list.length + ' files',
            '<ol class="sfbf__review-files">' +
              list.map(function (f, i) {
                var pi = prices[i] || {};
                var line = fmtRange(pi.lo, pi.hi, priceCur, pi.atMin);
                return '<li>' + esc(f.name) +
                  (f.quantity > 1 ? ' <strong>&times;' + f.quantity + '</strong>' : '') +
                  ' <em>' + f.dimsMm.join(' &times; ') + ' mm &middot; ' + esc(f.strength) +
                  ((f.scalePct && f.scalePct !== 100) ? ', ' + f.scalePct + '%' : '') +
                  ' &middot; ' + esc(f.colour || 'colour by the team') + '</em>' +
                  (f.fits === false ? ' <em>&middot; needs splitting</em>' : '') +
                  (line ? ' <b>' + line + '</b>' : '') +
                  '</li>';
              }).join('') +
            '</ol>'
          );
        } else {
          var one = list[0];
          var d = (one ? one.dimsMm : estimate.dimsMm) || [];
          html += row('File', esc(one ? one.name : estimate.file)) +
            row('Size', d.length === 3
              ? 'X ' + d[0] + ' &times; Y ' + d[1] + ' &times; Z ' + d[2] + ' mm' +
                (estimate.scalePct !== 100 ? ' <em>(scaled ' + estimate.scalePct + '%)</em>' : '')
              : '') +
            row('Material', esc((one && one.material) || estimate.material)) +
            row('Colour', esc((one && one.colour) || 'chosen by the team')) +
            row('Strength', esc(estimate.strength));
        }

        if (!estimate.fitsBuildVolume) {
          html += row('Note', 'Something is bigger than one build &mdash; we print it in sections and bond them.');
        }

        html += row(
          'Prices shown',
          '<strong>A reference estimate, not a final quote.</strong> ' +
          'The team confirms the price before anything is printed.'
        );
      } else {
        html += row('File', '<em>none attached &mdash; send it on WhatsApp after you submit</em>');
      }
      if (box) box.innerHTML = html;

      /* the price slot */
      renderPrice();

      /* everything the shop needs, in the contact-form body */
      var body = q('[data-sfbf-body]');
      if (body) {
        var lines = [];
        lines.push('BUILD WITH US — submitted from the website');
        lines.push('');
        lines.push('Name: ' + ((name && name.value) || ''));
        lines.push('WhatsApp: ' + ((phone && phone.value) || ''));
        lines.push('Email: ' + ((email && email.value) || '—'));
        if (student.yes) lines.push('STUDENT DISCOUNT REQUESTED — ID attached, verify before confirming');
        lines.push('');
        lines.push('What they want made:');
        lines.push((desc && desc.value) || '');
        lines.push('');
        if (estimate) {
          lines.push('Reference: ' + estimate.ref + '   (the customer quotes this on WhatsApp)');
          lines.push('');
          var band = fmtRange(priceLo, priceHi, priceCur, priceAtMin);
          lines.push('Range shown to the customer: ' + (band || 'none — the team quotes this'));
          lines.push('');
          lines.push('TEAM ONLY — not shown to the customer:');
          if (typeof price === 'number') {
            lines.push('  Base estimate (the low end): ' + fmtMoney(price, priceCur));
          }
          lines.push('  Filament: ' + estimate.gramsLo + '–' + estimate.gramsHi + ' g' +
                     '  (geometry figure ' + estimate.gramsEach + ' g)');
          lines.push('  Print time: ~' + Math.round(estimate.minutesEach) + ' min');
          lines.push('  Volume: ' + estimate.volumeCm3 + ' cm3 at ' + estimate.density + ' g/cm3');
          lines.push('  Profile assumed: 0.2 mm layers, 15% infill');
          lines.push('');
          // One line per part. dimsMm only exists on the order when there is a
          // single file, so the per-file list is the thing to read.
          var parts = estimate.files || [];
          lines.push(parts.length === 1 ? 'The file:' : 'The files:');
          parts.forEach(function (f, i) {
            lines.push(
              '  ' + (i + 1) + '. ' + f.name + (f.quantity > 1 ? ' x' + f.quantity : '') +
              ' — X ' + f.dimsMm[0] + ' x Y ' + f.dimsMm[1] + ' x Z ' + f.dimsMm[2] + ' mm' +
              (f.scalePct !== 100 ? ' (scaled ' + f.scalePct + '%)' : '')
            );
            lines.push('     ' + f.material + ' · ' + f.strength +
              ' · ' + (f.colour || 'colour by the team') +
              (f.fits === false ? ' · NEEDS SPLITTING' : ''));
          });
          lines.push('');
          lines.push('Geometry estimate, not a real slice. The band runs upward from it: a real slice adds material, it does not save any.');
          lines.push('The customer was shown a RANGE, never a single figure.');
          lines.push('The files are in Telegram and linked below.');
        } else {
          lines.push('No file attached — description only.');
        }
        body.value = lines.join('\n');
      }

      /* Both end buttons hand over the same pre-written message, built by the
         shared template in sf-instant-slicer.js so the two never drift. */
      var who = {
        name: (name && name.value) || '',
        email: (email && email.value) || '',
        description: (desc && desc.value) || ''
      };
      var msg = (window.SFSlicer && window.SFSlicer.waTemplate)
        ? window.SFSlicer.waTemplate(estimate, who)
        : 'Hi SolidForms — I just submitted a request on your site.';
      var href = 'https://wa.me/' + String(cfg.wa || '').replace(/\D/g, '') +
        '?text=' + encodeURIComponent(msg);

      root.querySelectorAll('[data-sfbf-wa]').forEach(function (a) {
        if (cfg.wa) a.href = href;
      });

      // Survives the reload that Shopify does on submit, so the success screen
      // can offer the same pre-written message.
      try { sessionStorage.setItem('sfbf:wa', href); } catch (e) {}
    }

    /* ------------------------------------------------------------ wiring */

    root.addEventListener('sfsl:price', function (e) {
      price = e.detail ? e.detail.total : null;
      prices = e.detail ? e.detail.items : [];
      priceLo = e.detail ? e.detail.lo : null;
      priceHi = e.detail ? e.detail.hi : null;
      priceCur = (e.detail && e.detail.currency) || 'EGP';
      priceAtMin = !!(e.detail && e.detail.atMin);
      renderPrice();
      if (current === 2) renderReview();
    });

    root.addEventListener('sfsl:estimate', function (e) {
      estimate = e.detail;
      var badge = q('[data-sfbf-file-state]');
      if (badge) {
        badge.textContent = estimate ? 'Measured' : 'Optional';
      }
      price = estimate ? undefined : null;
      if (!estimate) { priceLo = null; priceHi = null; }
      renderPrice();
      if (current === 2) renderReview();
    });

    /* ------------------------------------------------------------ draft */

    var restoring = false;
    var saveTimer = null;

    function draftFields() {
      var out = {};
      ['name', 'phone', 'email', 'description'].forEach(function (k) {
        var el = field(k);
        if (el) out[k] = el.value || '';
      });
      return out;
    }

    function hasContent(f, hadFiles) {
      return hadFiles || Object.keys(f).some(function (k) { return (f[k] || '').trim(); });
    }

    function saveText() {
      if (restoring) return;
      var fields = draftFields();
      var models = sliceApi('getFiles') || [];
      if (!hasContent(fields, models.length)) {
        try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) {}
        return;
      }
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
          v: 1,
          at: Date.now(),
          step: current,
          furthest: furthest,
          fields: fields,
          student: student.yes,
          /* The student ID is a photo of an identity document. It is uploaded
             and then forgotten deliberately — never written to disk here. */
          settings: sliceApi('getSettings') || []
        }));
      } catch (e) { /* private mode, or full — the form still works */ }
    }

    function sliceApi(fn, arg1, arg2) {
      var tool = root.querySelector('[data-sf-slicer]');
      var api = tool && tool.sfSlicer;
      return api && api[fn] ? api[fn](arg1, arg2) : null;
    }

    /* File bytes are saved separately and only when the set actually changes —
       writing 40 MB on every slider nudge would be absurd. */
    var lastFileSig = '';
    function saveFiles() {
      if (restoring) return;
      var models = sliceApi('getFiles') || [];
      var pics = sliceApi('getPhotos') || [];
      var sig = models.concat(pics).map(function (f) {
        return f ? f.name + ':' + f.size : '-';
      }).join('|');
      if (sig === lastFileSig) return;
      lastFileSig = sig;

      if (!models.length && !pics.length) return dbClear();
      Promise.all([
        Promise.all(models.filter(Boolean).map(toRecord)),
        Promise.all(pics.filter(Boolean).map(toRecord))
      ]).then(function (r) {
        return dbPut({ models: r[0], photos: r[1] });
      }).catch(function () { /* over quota: the text draft still survives */ });
    }

    function save() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(function () { saveText(); saveFiles(); }, 400);
    }

    function restoreDraft() {
      var raw = null;
      try { raw = sessionStorage.getItem(DRAFT_KEY); } catch (e) { return; }
      if (!raw) return;

      var d;
      try { d = JSON.parse(raw); } catch (e) { return clearDraft(); }
      if (!d || d.v !== 1) return clearDraft();

      restoring = true;
      Object.keys(d.fields || {}).forEach(function (k) {
        var el = field(k);
        if (el && d.fields[k]) el.value = d.fields[k];
      });
      if (d.student) setStudent(true);

      var wanted = Math.max(0, Math.min(steps.length - 1, d.step || 0));
      furthest = Math.max(furthest, d.furthest || 0);

      dbGet().then(function (rec) {
        if (!rec || !(rec.models || []).length) return null;
        return new Promise(function (resolve) {
          sliceApi('addFiles', rec.models.map(toFile), resolve);
        }).then(function () {
          if ((rec.photos || []).length) sliceApi('addPhotos', rec.photos.map(toFile));
          if (d.settings && d.settings.length) sliceApi('applySettings', d.settings);
        });
      }).catch(function () {
        /* The bytes are gone but the answers are not. Step 3 reviews files that
           no longer exist, so drop back to where they can be re-attached. */
        if (wanted > 1) wanted = 1;
      }).then(function () {
        restoring = false;
        lastFileSig = '';
        show(wanted);
        if (hasContent(d.fields || {}, (sliceApi('getFiles') || []).length)) notifyRestored();
      });
    }

    /* Say what happened, once. A form that silently refills itself is unnerving;
       one that says "your draft is back" is not. */
    function notifyRestored() {
      var slot = root.querySelector('[data-sfbf-restored]');
      if (!slot) return;
      slot.hidden = false;
      var kill = slot.querySelector('[data-sfbf-restored-dismiss]');
      if (kill) {
        kill.addEventListener('click', function () {
          slot.hidden = true;
          // Hold off saving while we tear down: resetting the slicer emits an
          // estimate event, which would re-save the very draft we are dropping.
          restoring = true;
          form.reset();
          sliceApi('reset');
          estimate = null; price = null; priceLo = null; priceHi = null;
          setStudent(false);
          renderPrice();
          show(0);
          clearTimeout(saveTimer);
          restoring = false;
          lastFileSig = '';
          clearDraft();
        });
      }
    }

    root.addEventListener('input', save, true);
    root.addEventListener('change', save, true);
    root.addEventListener('sfsl:estimate', save);
    restoreDraft();

    root.querySelectorAll('[data-sfbf-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (validateStep(current)) { show(current + 1); save(); }
      });
    });
    root.querySelectorAll('[data-sfbf-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { show(current - 1); });
    });
    rails.forEach(function (el, i) {
      el.addEventListener('click', function () {
        if (i <= furthest) show(i);
      });
    });

    // Clear an error the moment they start fixing it.
    root.querySelectorAll('[data-sfbf-required]').forEach(function (el) {
      el.addEventListener('input', function () {
        if (el.closest('.sfbf__field').classList.contains('is-invalid')) setError(el, '');
      });
    });

    /* ------------------------------------------------------- submitting */

    /*
     * Shopify's contact form cannot carry an attachment, so before we let it
     * submit we hand the file to our own endpoint. That parks it in Shopify
     * Files (permanent link for the email) and drops the file itself into the
     * team's Telegram. The returned link is written into the email body.
     *
     * If any of that fails we submit anyway — the customer must never be stuck
     * behind our plumbing, and the WhatsApp route still carries the file.
     */
    /*
     * Hand the submission to our own endpoint. Every submission goes through
     * here now, not only the ones carrying a file: a description-only request
     * is still a customer waiting for an answer, and it used to reach nothing
     * but the contact-form email — no Telegram, no WhatsApp confirmation, and
     * no order in 3D-Pi.
     */
    function uploadFile(ref) {
      var tool = root.querySelector('[data-sf-slicer]');
      var files = tool && tool.sfSlicer && tool.sfSlicer.getFiles ? tool.sfSlicer.getFiles() : [];
      if (!cfg.uploadUrl) return Promise.resolve(null);

      var meta = {
        ref: (estimate && estimate.ref) || ref,
        file: estimate ? estimate.file : null,
        files: estimate ? estimate.files : [],
        name: (field('name') || {}).value || '',
        phone: (field('phone') || {}).value || '',
        email: (field('email') || {}).value || '',
        description: ((field('description') || {}).value || '').slice(0, 1500),
        material: estimate ? estimate.material : null,
        strength: estimate ? estimate.strength : null,
        infillPct: estimate ? estimate.infillPct : null,
        scalePct: estimate ? estimate.scalePct : null,
        photoCount: (tool.sfSlicer.getPhotos ? tool.sfSlicer.getPhotos().length : 0),
        student: student.yes && !!student.file,
        gramsLo: estimate ? estimate.gramsLo : null,
        gramsHi: estimate ? estimate.gramsHi : null,
        minutes: estimate ? estimate.minutesEach : null,
        fits: estimate ? estimate.fitsBuildVolume : null,
        // What the customer was actually shown. The server re-prices via
        // 3D-Pi, but recording this means the digest is honest about the
        // figure on screen even on a run where 3D-Pi was unreachable.
        price: (typeof price === 'number' ? price : null),
        priceLo: priceLo,
        priceHi: priceHi,
        priceCurrency: priceCur,
        prices: prices.slice()
      };

      // Multipart, so the whole order — every part and the answers that go with
      // it — arrives in one request and becomes one 3D-Pi order.
      var form = new FormData();
      form.append('meta', JSON.stringify(meta));
      files.forEach(function (f) { if (f) form.append('files', f, f.name); });
      var pics = tool.sfSlicer.getPhotos ? tool.sfSlicer.getPhotos() : [];
      pics.forEach(function (p) { if (p) form.append('photos', p, p.name); });
      if (student.yes && student.file) form.append('student_id', student.file, student.file.name);

      var ctrl = new AbortController();
      var kill = setTimeout(function () { ctrl.abort(); }, 180000);

      return fetch(cfg.uploadUrl, { method: 'POST', body: form, signal: ctrl.signal })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (j) { clearTimeout(kill); return j; });
    }

    function setBusy(on) {
      var btn = root.querySelector('[data-sfbf-submit]');
      if (!btn) return;
      btn.disabled = on;
      if (on) {
        btn.dataset.label = btn.textContent;
        btn.textContent = cfg.sendingLabel || T('sending', 'Sending your file…');
      } else if (btn.dataset.label) {
        btn.textContent = btn.dataset.label;
      }
    }

    if (form) {
      var submitting = false;
      form.addEventListener('submit', function (e) {
        if (submitting) return; // our own second pass — let it through

        /*
         * Take control of the submit before doing anything else. If any of the
         * work below throws, the browser must not fall back to submitting the
         * form natively: that navigates away mid-upload, which aborts the file
         * transfer and loses the order. We decide when to submit, always.
         */
        e.preventDefault();

        try {
          if (!validateStep(0)) return show(0);
          if (!validateStep(1)) return show(1);
          renderReview();
        } catch (err) {
          // Better a plainer submission than none — the customer still reaches
          // the team, and the console keeps the detail for us.
          if (window.console) console.error('[sfbf] review failed:', err);
        }

        /*
         * Every submission gets a reference, whether or not a file came with
         * it — so the customer always leaves with something to quote at us and
         * the team always has something to match against. A 3D-Pi order number
         * replaces it when there was a file to price.
         */
        var ref = (estimate && estimate.ref) || makeRef();

        function remember(number, isOrder) {
          try {
            if (number) {
              sessionStorage.setItem('sfbf:order', number);
              sessionStorage.setItem('sfbf:order-kind', isOrder ? 'order' : 'ref');
            }
          } catch (err) {}
        }

        var body = q('[data-sfbf-body]');
        if (body && ref) body.value += '\nREFERENCE: ' + ref;

        // With no endpoint configured there is nothing to hand it to, so the
        // contact form carries it alone.
        if (!cfg.uploadUrl) {
          remember(ref, false);
          clearDraft();
          submitting = true;
          form.submit();
          return;
        }

        setBusy(true);
        uploadFile(ref).then(function (result) {
          if (body) {
            if (estimate) {
              body.value += '\n' + (result && result.fileUrl
                ? 'FILE: ' + result.fileUrl
                : 'FILE: upload did not complete — ask the customer to send it on WhatsApp.');
            }
            if (result && result.orderNumber) {
              body.value += '\n3D-PI ORDER: ' + result.orderNumber;
            }
          }
          // The order number if we got one, otherwise the reference — never
          // nothing, because the confirmation screen has to say something.
          remember((result && result.orderNumber) || ref, !!(result && result.orderNumber));
          clearDraft();
          submitting = true;
          setBusy(false);
          form.submit();
        });
      });
    }

    show(0);
  }

  function upgradeAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sf-build-flow]');
    for (var i = 0; i < nodes.length; i++) Flow(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { upgradeAll(); });
  } else {
    upgradeAll();
  }
  document.addEventListener('shopify:section:load', function (e) { upgradeAll(e.target); });
})();
